import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import { editObject, getObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import { useLocation, useOutletContext } from 'react-router-dom'
import Form from '../../form/Form'
import Toolbar from '../../toolbar/Toolbar'
import { getCompanyFields, getCompanyTabs } from './CompanyFields'
import { fileToBase64, handleFileUpload, updateAttachments } from '../../../utils/documentManager'

const applyEmailTemplateImages = (html, company) => {
  if (!html) return html

  const el = document.createElement('html')
  el.innerHTML = html

  const setImg = (selector, src) => {
    const img = el.querySelector(selector)
    if (img) img.src = src
  }

  setImg('#img_header', company.img_header)
  setImg('#img_body', company.img_body)

  const header = el.querySelector('#header_cell')
  if (header) header.style.backgroundColor = company.header_color

  const footer = el.querySelector('#footer_cell')
  if (footer) {
    footer.style.backgroundColor = company.footer_color
    Array.from(footer.getElementsByTagName('p')).forEach((p) => {
      p.style.color = company.footerFont_color
    })
  }

  return el.innerHTML
}

const Company = ({
  modeProps = 'normal',
  companyAdd = {},
  setStepFailed,
  setCompanyAdd,
  check,
  setErrorsCompany,
  setCheck,
  onValidStep
}) => {
  const [company, setCompany] = useState(companyAdd)
  const [mode, setMode] = useState(null)
  const [file, setFile] = useState(null)
  const [fileCachet, setFileCachet] = useState(null)
  const [attach, setAttach] = useState(null)
  const [cachet, setCachet] = useState(null)
  const [isSubmit, setIsSubmit] = useState(false)
  const [isFormError, setIsFormError] = useState(false)

  const toastId = useRef(null)
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()
  const userRight = useOutletContext()

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: 'Entreprise',
      objectType: 'company',
      actions: { edit: true, audits: true }
    }),
    [userRight]
  )

  const options = useMemo(
    () => ({
      tabs: getCompanyTabs(modeProps),
      fields: getCompanyFields({ attach, cachet }),
      tabDefault: '1',
      data: company,
      location
    }),
    [modeProps, company, location, attach, cachet]
  )

  const updateAttachment = useCallback(
    (fileObj, type, setFileState, currentAttachment) => {
      if (!fileObj || !(mode === 'edit' || modeProps === 'wizard')) return

      if (currentAttachment?.file?.data === fileObj) return

      const newAttach = {
        type,
        file: { data: URL.createObjectURL(fileObj), type: 'file' },
        id: currentAttachment?.id
      }

      const newFiles = [...(company.attachments?.filter((a) => a.type !== type) || []), newAttach]

      setFileState(fileObj)
      setCompany((prev) => ({ ...prev, attachments: newFiles }))

      if (type === 'logo') setAttach(newAttach)
      if (type === 'cachet') setCachet(newAttach)
    },
    [company.attachments, mode, modeProps]
  )

  const getImageConverted = useCallback(
    async (fileKey) => {
      const file = company[fileKey]
      if (!file || typeof file === 'string') return
      const base64 = await fileToBase64(file)
      setCompany((prev) => ({ ...prev, [fileKey]: base64 }))
    },
    [company]
  )

  useEffect(() => {
    if (modeProps === 'wizard') {
      setCompany(companyAdd)
    }
  }, [companyAdd, modeProps])

  useEffect(() => {
    const templates = [
      'invoice_email_template',
      'attestation_email_template',
      'intervention_email_template',
      'quote_email_template',
      'credit_email_template'
    ]

    let updated = false
    const updatedCompany = { ...company }

    templates.forEach((tpl) => {
      if (company[tpl] && typeof company[tpl] === 'string') {
        const newHTML = applyEmailTemplateImages(company[tpl], company)
        if (newHTML !== company[tpl]) {
          updatedCompany[tpl] = newHTML
          updated = true
        }
      }
    })

    if (updated) setCompany(updatedCompany)
  }, [company.img_header, company.img_body, company.header_color, company.footer_color, company.footerFont_color])

  useEffect(() => {
    if (!company.is_siege) {
      setCompany((prev) => ({
        ...prev,
        office_address: prev.address1,
        office_address2: prev.address2,
        office_zip: prev.zip,
        office_city: prev.city
      }))
    }
  }, [company.is_siege, company.address1, company.address2, company.zip, company.city])

  useEffect(() => {
    if (modeProps !== 'wizard') return

    setErrorsCompany(isFormError)
    setStepFailed(isFormError)
  }, [isFormError, modeProps])

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await getObject('company', user.company_id)
        const attachments = res.data.attachments || []
        setCompany(res.data)
        setAttach(attachments.find((a) => a.type === 'logo') || null)
        setCachet(attachments.find((a) => a.type === 'cachet') || null)
      } catch (err) {}
    }

    if (modeProps !== 'normal') {
      setCompany((prev) => ({ ...prev, attachments: [] }))
      return
    }

    if (location.pathname === '/company/edit') {
      setMode('edit')
      fetchCompany()
    } else if (location.pathname === '/company') {
      setMode('view')
      fetchCompany()
    }
  }, [location.pathname, user, modeProps])

  useEffect(() => {
    if (!company.file) return
    if (file === company.file) return
    updateAttachment(company.file, 'logo', setFile, attach)
  }, [company.file, attach, file, updateAttachment])

  useEffect(() => {
    if (!company.fileCachet) return
    if (fileCachet === company.fileCachet) return
    updateAttachment(company.fileCachet, 'cachet', setFileCachet, cachet)
  }, [company.fileCachet, cachet, fileCachet, updateAttachment])

  useEffect(() => {
    getImageConverted('img_header')
  }, [company.img_header, getImageConverted])
  useEffect(() => {
    getImageConverted('img_body')
  }, [company.img_body, getImageConverted])

  useEffect(() => {
    if (!isSubmit) return

    const submitCompany = async () => {
      if (modeProps === 'normal') {
        if (location.pathname === '/company/edit') {
          let updatedCompany = { ...company }

          if (file) {
            const uploadedLogo = await handleFileUpload(file, user, 'logo', 'companies', company)
            if (uploadedLogo) updatedCompany.attachments = updateAttachments(updatedCompany.attachments, uploadedLogo)
          }

          if (fileCachet) {
            const uploadedCachet = await handleFileUpload(fileCachet, user, 'cachet', 'companies', company)
            if (uploadedCachet)
              updatedCompany.attachments = updateAttachments(updatedCompany.attachments, uploadedCachet)
          }

          toastId.current = toast.loading('Enregistrement en cours')

          try {
            const res = await editObject('company', user.company_id, {
              ...updatedCompany,
              lastModBy: user.email
            })

            toast.update(toastId.current, {
              type: 'success',
              render: 'Enregistrement terminé',
              autoClose: 2000,
              isLoading: false
            })
            setCompany(res.data)
            history('/company')
          } catch (e) {
            toast.update(toastId.current, {
              type: 'error',
              render: e.response?.statusText || e.message,
              autoClose: 2000,
              isLoading: false
            })
          }
        }
      } else if (modeProps === 'wizard') {
        setCompanyAdd((prev) => ({
          ...prev,
          ...company,
          type: 'micro',
          attachments: null,
          header_color: '#8AA1FF',
          footer_color: '#2B335D',
          footerFont_color: '#FFFFFF',
          is_free_account: true
        }))
        setCheck(false)
        setErrorsCompany(false)
      }

      setIsSubmit(false)
    }

    submitCompany()
  }, [
    isSubmit,
    company,
    modeProps,
    location.pathname,
    user,
    file,
    fileCachet,
    setCompanyAdd,
    setCheck,
    setErrorsCompany,
    history
  ])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexGrow: 1
      }}
    >
      {modeProps === 'normal' && <Toolbar options={toolbar} object={company} />}
      <Form
        modeProps={modeProps}
        checkForm={check}
        setCheckForm={setCheck}
        mode={mode}
        setFormError={setIsFormError}
        options={options}
        setDataParent={(data) => {
          setCompany(data)
          if (modeProps === 'wizard') {
            setCompanyAdd(data)
          }
        }}
        setIsSubmitParent={setIsSubmit}
        onValidStep={onValidStep}
      />
    </Box>
  )
}

export default Company
